// React import
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import queryString from 'query-string';

// MUI imports
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InputAdornment from '@mui/material/InputAdornment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import { grey, red } from '@mui/material/colors';

// FontAwesomeIcon imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';

// Local import
import AddressInput from '../../AddressInput.js';
import { ProductDescription } from '../../utils/ProductHelperComponents.js';
import Product from '../../Product.js';
import LegEditor from '../../LegEditor.js'
import PRODUCT_TYPES from '../../ProductTypes.js';
import { checkProductErrors, createEmptyProduct } from '../utils/product.js';
import { minimizeProducts } from '../utils/opportunity.js';
import PickOwner from '../components/OpportunitiesFilter.owner.js';

function AccountDetailForm(props) {
    const { mode, accounts, suppliers, accountData, setAccountData, seaPorts, me, onSave, accesstoken, organization } = props;
    const [transportModes, setTransportModes] = useState(['']);
    const [ expanded, setExpanded ] = useState(['company', 'contacts', 'transportation', 'owner', 'settings']);
    const navigate = useNavigate();
    const location = useLocation();

    const initialData = queryString.parse(location.search).initialData;
    
    useEffect(() => {
        if (initialData) {
            setAccountData(JSON.parse(atob(initialData)));
        }
    }, [ initialData ]);

    const handleAddContact = () => {
        setAccountData({
            ...accountData,
            contacts: [...accountData.contacts, { firstName: '', lastName: '', email: '', phone: '', title: '' }]
        });
    };

    const handleUpdateContact = ({ index, field, value }) => {
        const newContacts = accountData.contacts.map((contact, i) => {
            if (i === index) {
                return { ...contact, [field]: value };
            }
            return contact;
        });
        setAccountData({
            ...accountData,
            contacts: newContacts
        });
    }

    const handleAddTransportMode = () => {
        setTransportModes([...transportModes, '']);
    };

    // Replace with your actual logic
    const isTMSIntegrationEnabled = true;

    const [ branchCode, branchName, accountUniqueId ] = (() => {
        if ((accountData.uniqueId || '').split(':').length > 1) return accountData.uniqueId.split(':');
        return ['', '', accountData.uniqueId || ''];
    })();

    return (
        <div>
            <Accordion expanded={expanded.includes('company')} onChange={() => setExpanded(e => _.xor(e, ['company']))}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Company details
                </AccordionSummary>
                <AccordionDetails>
                    <Autocomplete
                        sx={{ mb: 0 }}
                        fullWidth
                        disabled={!!accountData.id}
                        value={accountData.name || ''}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                const name = typeof newValue === 'string' ? newValue : newValue.name;
                                setAccountData(ad => ({...ad, name: _.trim(name)}));
                            }
                        }}
                        inputValue={accountData.name || ''}
                        onInputChange={(event, newInputValue) => {
                            const trimmedStart = newInputValue.replace(/^\s+/, '');
                            setAccountData(ad => ({...ad, name: trimmedStart}));
                        }}
                        freeSolo
                        options={accountData.name?.length >= 2 ?
                            _.sortBy(
                                _.uniqBy(
                                    (mode === 'accounts' ? accounts : suppliers)?.map(a => ({ name: a.name, uniqueId: a.uniqueId })) || [],
                                    a => a.name
                                ),
                                a => a.name
                            ) : []}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                        filterOptions={(options, state) => {
                            const input = state.inputValue.toLowerCase();
                            return options.filter(o => o.name?.toLowerCase().includes(input));
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.name}>
                                <Box>
                                    <Typography sx={{ fontSize: '13px' }}>{option.name}</Typography>
                                    {option.uniqueId && (
                                        <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>{option.uniqueId}</Typography>
                                    )}
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Company"
                                onBlur={() => {
                                    setAccountData(ad => ({...ad, name: _.trim(ad.name || '')}));
                                }}
                            />
                        )}
                    />
                    {(() => {
                        if (!!accountData.id) return null;
                        const existing = mode === 'accounts' ? accounts.find(a => a.name?.toUpperCase() === accountData?.name?.toUpperCase()) : suppliers.find(s => s.name?.toUpperCase() === accountData?.name?.toUpperCase());
                        if (existing) {
                            return (
                                <Box sx={{ mt: 2 }}>
                                    <Alert 
                                        severity='warning' 
                                        sx={{ mt: 1, mb: 1 }}
                                        action={
                                            <Button 
                                                variant='text' 
                                                color='inherit' 
                                                size='small'
                                                onClick={() => {
                                                    navigate(`/organization/${organization.alias}/${mode}/${existing.id}/details`, { replace: true });
                                                }}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Jump to existing {mode === 'accounts' ? 'account' : 'supplier'}
                                            </Button>
                                        }
                                    >
                                        {mode === 'accounts' ? 'Account' : 'Supplier'} with the same name already exists.
                                    </Alert>
                                </Box>
                            );
                        }
                        return null;
                    })()}
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id='industry-label'>Industry</InputLabel>
                        <Select
                            labelId='industry-label'
                            id='industry-select'
                            value={accountData.industry || ''}
                            label='Industry'
                            onChange={(e) => setAccountData(ad => ({...ad, industry: e.target.value }))}
                            sx={{ mb:2 }}
                        >
                            {INDUSTRIES.map(industry => (
                                <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <AddressInput
                        value={(accountData.address || {}).text || ''}
                        textFieldProps={{
                            autoFocus: false,
                            style: { borderRadius: '0px', width: '100%' },
                            placeholder: '',
                            id: 'where-from',
                            label: 'Address',
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <LocationOnIcon />
                                </InputAdornment>
                            )
                        }}
                        onChange={(v) => {
                            setAccountData(ad => ({...ad, address: v }));
                        }}
                        seaPorts={seaPorts}
                    />
                    {accountData?.address?.text && !accountData?.address?.id && (
                        <Alert severity='error' sx={{ mt: 1 }}>
                            Address is incorrect. Please double check the address and fix it before saving.
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'row-reverse', borderTop: `1px solid ${grey[300]}`, marginTop: '24px' }}>
                        <Button
                            variant='text'
                            color='primary'
                            sx={{ marginTop: '12px' }}
                            disabled={false}
                            onClick={() => {
                                setExpanded(_.without(expanded, 'company'))
                            }}
                        >
                            Next
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion sx={{ mt: 1 }} expanded={expanded.includes('contacts')} onChange={() => setExpanded(e => _.xor(e, ['contacts']))}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Contact details
                </AccordionSummary>
                <AccordionDetails>
                    {accountData.contacts.map((contact, index) => (
                        <Box key={index} sx={{ display: 'flex', width: '100%', flexDirection: 'column', mb: 2 }}>
                            <Typography variant='caption' component={Box} sx={{ color: grey[500], mb: 1 }}>
                                <b>Contact {index + 1}</b>
                            </Typography>
                            <Box sx={{ display: 'flex', width: '100%' }}>
                                {['firstName', 'lastName', 'email', 'phone', 'title'].map(field => (
                                    <TextField
                                        key={field}
                                        label={_.startCase(field)}
                                        value={contact[field]} 
                                        onChange={e => handleUpdateContact({ index, field, value: e.target.value })}
                                        sx={{ mr: 1, flex: 1 }} 
                                    />
                                ))}
                                <IconButton
                                    color='error'
                                    disabled={ accountData.contacts.length === 1 }
                                    onClick={() => setAccountData(ad => ({...ad, contacts: ad.contacts.filter((c, i) => i !== index) }))}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}
                    <Button onClick={handleAddContact}>+ Add contact</Button>
                    <Box sx={{ display: 'flex', flexDirection: 'row-reverse', borderTop: `1px solid ${grey[300]}`, marginTop: '24px' }}>
                        <Button
                            variant='text'
                            color='primary'
                            sx={{ marginTop: '12px' }}
                            disabled={false}
                            onClick={() => {
                                setExpanded(_.without(expanded, 'contacts'))
                            }}
                        >
                            Next
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion sx={{ mt: 1 }} expanded={expanded.includes('transportation')} onChange={() => setExpanded(e => _.xor(e, ['transportation']))}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Transportation Methods & Cargo
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'flex', flexDirection: 'column' }}>
                    {['sea', 'air', 'land'].map(method => {
                        const data = accountData.transportationMethods?.[method] || {};
                        const active = !!data?.active;
                        return (
                            <Box key={method}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={active}
                                            onChange={() => setAccountData(ad => ({...ad, transportationMethods: { 
                                                ...ad.transportationMethods, 
                                                    [method]: {...data, active: !active } }}))}
                                            color='primary'
                                        />
                                    }
                                    label={`Account supports ${method} cargo.`}
                                />
                                {active && (
                                    <Routes
                                        transportationMethod={method}
                                        routes={data.routes || []} 
                                        setRoutes={routes => setAccountData(ad => ({...ad, transportationMethods: { ...ad.transportationMethods, [method]: { ...data, routes } }}))} 
                                        seaPorts={seaPorts}
                                    />
                                )}

                                {active && (
                                    <CargoProducts
                                        accesstoken={accesstoken}
                                        organization={organization}
                                        transportationMethod={method}
                                        products={data.products || []} 
                                        setProducts={products => setAccountData(ad => ({...ad, transportationMethods: { ...ad.transportationMethods, [method]: { ...data, products } }}))} 
                                    />
                                )}
                            </Box>
                        );
                    })}
                    <Box sx={{ display: 'flex', flexDirection: 'row-reverse', borderTop: `1px solid ${grey[300]}`, marginTop: '24px' }}>
                        <Button
                            variant='text'
                            color='primary'
                            sx={{ marginTop: '12px' }}
                            disabled={false}
                            onClick={() => {
                                setExpanded(_.without(expanded, 'transportation'))
                            }}
                        >
                            Next
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion sx={{ mt: 1 }} expanded={expanded.includes('owner')} onChange={() => setExpanded(e => _.xor(e, ['owner']))}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Account Owner
                </AccordionSummary>
                <AccordionDetails>
                    <PickOwner 
                        org={organization}
                        me={me}
                        filter={{ members: [accountData.owner] }}
                        setFilter={(filter) => setAccountData(ad => ({...ad, owner: filter.members[0]}))}
                        singlePick={true}
                        disabled={accountData.owner !== me.email && !me.isAdmin}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'row-reverse', borderTop: `1px solid ${grey[300]}`, marginTop: '24px' }}>
                        <Button
                            variant='text'
                            color='primary'
                            sx={{ marginTop: '12px' }}
                            disabled={false}
                            onClick={() => {
                                setExpanded(_.without(expanded, 'owner'))
                            }}
                        >
                            Next
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>

            {isTMSIntegrationEnabled && (
                <Accordion sx={{ mt: 1 }} expanded={expanded.includes('settings')} onChange={() => setExpanded(e => _.xor(e, ['settings']))}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        Settings
                    </AccordionSummary>
                    <AccordionDetails>
                        <TextField
                            sx={{ mb: 2 }}
                            fullWidth
                            value={branchCode}
                            onChange={event => {
                                setAccountData(ad => ({ ...ad, uniqueId: `${event.target.value}:${branchName}:${accountUniqueId}` }));
                            }}
                            label={'Branch Code'}
                        />
                        <TextField
                            sx={{ mb: 2 }}
                            fullWidth
                            value={branchName}
                            onChange={event => {
                                setAccountData(ad => ({ ...ad, uniqueId: `${branchCode}:${event.target.value}:${accountUniqueId}` }));
                            }}
                            label={'Branch Name'}
                        />
                        <TextField
                            fullWidth
                            value={accountUniqueId}
                            onChange={event => {
                                setAccountData(ad => ({ ...ad, uniqueId: `${branchCode}:${branchName}:${event.target.value}` }));
                            }}
                            label={'Unique Id'}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'row-reverse', borderTop: `1px solid ${grey[300]}`, marginTop: '24px' }}>
                            <Button
                                variant='text'
                                color='primary'
                                sx={{ marginTop: '12px' }}
                                disabled={false}
                                onClick={() => {
                                    setExpanded([]);
                                    onSave();
                                }}
                            >
                                Save
                            </Button>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}
      </div>
    );
}

export default AccountDetailForm;

function Routes(props) {
    const { routes, setRoutes, transportationMethod, seaPorts } = props;

    return (
        <Box sx={{ margin: '0px 24px', border: '1px solid #eee', padding: '12px', mb: 1 }}>
            <Typography variant='caption' component={Box} sx={{ color: grey[500], mb: 1 }}>
                Routes
            </Typography>
            <LegEditor 
                transportationMethod={transportationMethod}
                routes={_.isEmpty(routes) ? [{ source: null, destination: null }] : routes}
                setRoutes={setRoutes}
                setTransportationMethod={null}
                autoPopulateSource={true}
                seaPorts={seaPorts}
            />
        </Box>
    )
}

function CargoProducts(props) {
    const { products, setProducts, transportationMethod, accesstoken, organization } = props;
    const [ addProduct, setAddProduct ] = useState(null);
    const [ errors, setErrors ] = useState([]);

    function returnSetter({propName, errorName}) {
        return (v) => {
            if (errorName) setErrors(_.without(errors, `product:0:${errorName}`));
            if (propName === 'type') {
                setAddProduct({ ...createEmptyProduct({ transportationMethod }), [propName]: v });
            } else {
                setAddProduct(ap => ({ ...ap, [propName]: v }));
            }
        }
    }

    const setCmOrInches = returnSetter({ propName: 'cmOrInches' });
    const setKgOrLbs = returnSetter({ propName: 'kgOrLbs' });
    const setCelsiusOrFahrenheit = returnSetter({ propName: 'celsiusOrFahrenheit' });
    const setWeight = returnSetter({ propName: 'weight', errorName: 'weight' });
    const setVolume = returnSetter({ propName: 'volume', errorName: 'volume' });
    const setWidth = returnSetter({ propName: 'width', errorName: 'width' });
    const setHeight = returnSetter({ propName: 'height', errorName: 'height' });
    const setLength = returnSetter({ propName: 'length', errorName: 'length' });
    const setType = returnSetter({ propName: 'type', errorName: 'type' });
    const setNonStackable = returnSetter({ propName: 'nonstackable' });
    const setDangerous = returnSetter({ propName: 'dangerous' });
    const setDangerousClass = returnSetter({ propName: 'dangerousClass', errorName: 'dangerousClass' });
    const setDangerousUNCode = returnSetter({ propName: 'dangerousUNCode', errorName: 'dangerousUNCode' });
    const setTemperature = returnSetter({ propName: 'temperature', errorName: 'temperature' });
    const setChargeableWeight = returnSetter({ propName: 'chargeableWeight', errorName: 'chargeableWeight' });
    const setQuantity = returnSetter({ propName: 'quantity', errorName: 'quantity' });
    const setDangerousMSDS = returnSetter({ propName: 'dangerousMSDS', errorName: 'dangerousMSDS' });
    const setCommodity = returnSetter({ propName: 'commodity' });

    return (
        <Box sx={{ margin: '0px 24px', border: '1px solid #eee', padding: '12px' }}>
            <Typography variant='caption' component={Box} sx={{ color: grey[500], mb: 1 }}>
                Product Quantity - Monthly ‘Estimate’
            </Typography>
            {products.map(product => (
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <Box sx={{flex: 1}}>
                        <ProductDescription key={product.id} product={product} allProducts={products} transportationMethod={transportationMethod} organization={organization} />
                    </Box>
                    <IconButton color='error' onClick={() => setProducts(products.filter(p => p.id !== product.id))}>
                        <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                </Box>
            ))}
            <Button 
                onClick={() => {
                    setAddProduct(createEmptyProduct({ transportationMethod }))
                    setErrors([]);
                }}>
                + Add product
            </Button>
            {addProduct && (
                <Dialog open={!!addProduct} onClose={() => setAddProduct(null)} fullWidth maxWidth={'lg'}>
                    <DialogTitle>Add Product</DialogTitle>
                    <DialogContent>
                        <Product 
                            key={addProduct.id}
                            transportationMethod={transportationMethod}
                            PRODUCT_TYPES={PRODUCT_TYPES}
                            _key={addProduct.id}
                            type={addProduct.type}
                            cmOrInches={addProduct.cmOrInches}
                            setCmOrInches={setCmOrInches}
                            width={addProduct.width}
                            setWidth={setWidth}
                            height={addProduct.height}
                            setHeight={setHeight}
                            length={addProduct.length}
                            setLength={setLength}
                            setType={setType}
                            kgOrLbs={addProduct.kgOrLbs}
                            setKgOrLbs={setKgOrLbs}
                            nonstackable={addProduct.nonstackable}
                            setNonStackable={setNonStackable}
                            dangerous={addProduct.dangerous}
                            setDangerous={setDangerous}
                            chargeableWeight={addProduct.chargeableWeight}
                            temperature={addProduct.temperature}
                            setTemperature={setTemperature}
                            setChargeableWeight={setChargeableWeight}
                            celsiusOrFahrenheit={addProduct.celsiusOrFahrenheit}
                            setCelsiusOrFahrenheit={setCelsiusOrFahrenheit}
                            dangerousClass={addProduct.dangerousClass}
                            dangerousUNCode={addProduct.dangerousUNCode}
                            setDangerousClass={setDangerousClass}
                            setDangerousUNCode={setDangerousUNCode}
                            dangerousMSDS={addProduct.dangerousMSDS}
                            setDangerousMSDS={setDangerousMSDS}
                            quantity={addProduct.quantity}
                            setQuantity={setQuantity}
                            weight={addProduct.weight}
                            setWeight={setWeight}
                            volume={addProduct.volume}
                            setVolume={setVolume}
                            commodity={addProduct.commodity}
                            setCommodity={setCommodity}
                            errors={errors.filter(e => e.startsWith('product:0')).map(e => e.split(':')[2])}
                            organization={organization}
                            accesstoken={accesstoken}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                const newErrors = [];
                                if (!addProduct.type) newErrors.push('type');
                                const e = checkProductErrors({ product: addProduct });
                                e.forEach(key => newErrors.push(key));
                                if (newErrors.length) {
                                    setErrors([...errors.filter(e => !e.startsWith('product:0')), ...newErrors.map(e => `product:0:${e}`)]);
                                    return;
                                }
                                setProducts(minimizeProducts({ products: [...products, addProduct] }));
                                setAddProduct(null);
                            }}
                        >
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
}

const INDUSTRIES = [
'Agriculture',
'Automotive',
'Aerospace',
'Banking and Finance',
'Biotechnology',
'Chemical Industry',
'Construction',
'Consumer Goods',
'Education',
'Electronics',
'Energy',
'Entertainment and Media',
'Fashion and Apparel',
'Food and Beverage',
'Healthcare and Pharmaceuticals',
'Hospitality and Tourism',
'Information Technology',
'Logistics and Transportation',
'Manufacturing',
'Mining and Extraction',
'Oil and Gas',
'Paper and Pulp',
'Real Estate',
'Retail and E-commerce',
'Telecommunications',
'Textiles',
'Utilities',
'Warehousing and Distribution',
'Waste Management',
'Wholesale Trade'];
